import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { ServerVarsService } from '../server-vars/server-vars.service';
import { CurrentLocationService } from '../service/current-location.service';
import { PlatformService } from '../universal/platform.service';
import { ServerService } from '../universal/server.service';
import { getCircularReplacer } from '../utils/circular-replacer';
import { Dictionary } from '../utils/dictionary.type';
import { ErrorLog, Log, LogDataMessage, LogLevel, LogMessage } from './log';

@Injectable({
    providedIn: 'root'
})
export class BasicLoggerService {
    private logQueue$ = new ReplaySubject<string>();
    private isSSR: boolean;
    private version: string;
    private serverLogFn: (body: any[], req: any) => void;
    private serverPort: string;

    constructor(
        private serverService: ServerService,
        private serverVars: ServerVarsService,
        currentLocation: CurrentLocationService,
        platform: PlatformService
    ) {
        this.isSSR = platform.isServer;
        this.version = serverVars.getVersion();
        this.serverLogFn = this.serverService.logFn;
        this.serverPort = currentLocation.url.port;
    }

    get logQueueObservable() {
        return this.logQueue$ as Observable<string | object>;
    }

    log(
        messages: LogMessage[],
        level: LogLevel,
        extras = {},
        isClientLogLevelAllowed = true,
        isLogLevelAllowed = true,
        logWithoutLogEndpointAnyway = false,
        encodeLogPayload = true,
        doNotLogToEndpoint = false
    ) {
        const clientDate = new Date();
        const messageStrings = messages.filter(m => typeof m === 'string');
        // make misc. log data more suitable for log handling and searching
        // convert to style { 'data_name': 'json_string_of_data'};
        // this should end up in the field `angular.data.data_name` in Kibana
        const messageData = messages
            .filter(m => typeof m !== 'string')
            .reduce((mergedDataMessages, m: LogDataMessage) => {
                if (m[1] instanceof Object) {
                    mergedDataMessages[m[0]] = JSON.stringify(m[1]);
                } else {
                    mergedDataMessages[m[0]] = m[1];
                }

                return mergedDataMessages;
            }, {}) as Dictionary<string>;

        const finalLog: Log = {
            level,
            message: messageStrings && messageStrings.join(' '),
            isSSR: this.isSSR,
            clientTimestamp: clientDate.toISOString(),
            url: this.isSSR ? this.serverService.request.url : window.location.href,
            userAgent: this.isSSR ? this.serverService.request.headers?.['User-Agent'] : window.navigator.userAgent,
            version: this.version,
            tabId: this.isSSR ? undefined : window['shp_TABID'],
            data: messageData,
            ...extras
        };

        // logging to client console
        if (isClientLogLevelAllowed && !this.isSSR) {
            if (finalLog.level === 'error' && (finalLog as ErrorLog).error) {
                console.error((finalLog as ErrorLog).error, ...messages);
            } else {
                console[level](...messages);
            }
        }

        // logging to graylog
        const env = this.serverVars.getEnvironment();
        // NOTE: Currently there is no /log endpoint when running in esbuild dev server mode (`ng serve`). Check back after https://github.com/angular/angular-cli/issues/26323
        const localEnvWithNoLogEndpoint = !this.isSSR && env === 'local' && window.location.port === '4000';

        if (isLogLevelAllowed) {
            if (!localEnvWithNoLogEndpoint || (localEnvWithNoLogEndpoint && logWithoutLogEndpointAnyway)) {
                this.sendLogToEndpoint(finalLog, encodeLogPayload, doNotLogToEndpoint);
            }
        }
    }

    private sendLogToEndpoint(log: Log, encodeLogPayload: boolean, doNotLogToEndpoint: boolean) {
        // clean the Log object from circular dependencies and
        // base64 encode it to satisfy security checks in the Cloudflare firewall
        // using decodeURIComponent(encodeURIComponent()) to convert latin1 (umlauts) to utf8
        const cleanedEncodedLog = !encodeLogPayload
            ? JSON.parse(JSON.stringify(log, getCircularReplacer()))
            : btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(log, getCircularReplacer()))));

        if (this.isSSR) {
            if (this.serverPort === '4000' || this.serverLogFn == null) {
                // do not use server.ts functionality when using local dev server (currently server.ts is not used by Vite dev server)
                // just print them to the console
                console.log('DEV-SERVER-LOG:', cleanedEncodedLog);
            } else {
                this.serverLogFn([cleanedEncodedLog], this.serverService.request);
            }
        } else if (!doNotLogToEndpoint) {
            this.logQueue$.next(cleanedEncodedLog);
        }
    }
}
